import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ManualAbsence } from "@msc/shared";

import {
  useDeleteManualAbsence,
  useManualAbsences,
} from "../../hooks/useAbsences.js";
import { useEmployees } from "../../hooks/useEmployees.js";
import { useSegments } from "../../hooks/useSegments.js";
import {
  GERMAN_DATE_FORMAT,
  formatIsoDate,
  toLocalDateString,
} from "../../lib/date.js";
import { AbsenceModal } from "./AbsenceModal.js";

/**
 * Abwesenheiten von Hand pflegen — der Ort für den Überblick und für das, was
 * viele Personen auf einmal betrifft (Betriebsferien).
 *
 * Für den Einzelfall gibt es den zweiten, näheren Weg: In der
 * Berichtsbearbeitung steht die Abwesenheit direkt bei jedem Ansprechpartner
 * (`AbsenceContactPanel`). Wer beim Schreiben merkt, dass die Workshop-Woche
 * des Kollegen fehlt, soll nicht die Seite wechseln müssen.
 *
 * Hier stehen **nur** die Hand-Einträge, nicht die aus Personio. Das ist keine
 * Unvollständigkeit, sondern die Grenze dieser Seite: Was in Personio steht,
 * wird dort geändert, und eine Liste, in der man die Hälfte der Zeilen nicht
 * anfassen kann, lädt zu genau diesem Versuch ein.
 */

/**
 * Vorbelegung des Fensters: ein Monat zurück, ein Jahr nach vorn.
 *
 * Der Blick zurück ist kein Komfort, sondern notwendig: Die erste Fassung
 * begann bei HEUTE, und damit war ein Eintrag für letzte Woche unsichtbar und
 * nicht mehr löschbar — er wirkte aber weiter auf die Kapazitätsrechnung. Beide
 * Grenzen sind deshalb verstellbar.
 */
function standardFenster(): { from: Date; to: Date } {
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  const to = new Date();
  to.setFullYear(to.getFullYear() + 1);
  return { from, to };
}

export function AbsencesTab() {
  const { t } = useTranslation();
  const [fenster, setFenster] = useState(standardFenster);
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const segments = useSegments();
  const employees = useEmployees(segmentId ?? undefined);
  const liste = useManualAbsences({
    segmentId: segmentId ?? undefined,
    from: toLocalDateString(fenster.from),
    to: toLocalDateString(fenster.to),
  });
  const loeschen = useDeleteManualAbsence();

  const [offen, setOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<ManualAbsence | null>(null);

  function oeffneNeu() {
    setBearbeitet(null);
    setOffen(true);
  }

  function oeffneBearbeiten(eintrag: ManualAbsence) {
    setBearbeitet(eintrag);
    setOffen(true);
  }

  const eintraege = liste.data ?? [];

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light">
        {t("pages.settings.absences.hint")}
      </Alert>

      <Group justify="space-between" align="flex-end">
        <Group align="flex-end">
          <Select
            label={t("pages.settings.absences.segmentFilter")}
            placeholder={t("pages.settings.absences.allSegments")}
            clearable
            data={(segments.data ?? []).map((s) => ({
              value: s.id,
              label: s.name,
            }))}
            value={segmentId}
            onChange={setSegmentId}
            w={220}
          />
          <DatePickerInput
            label={t("pages.settings.absences.windowFrom")}
            valueFormat={GERMAN_DATE_FORMAT}
            value={fenster.from}
            onChange={(from) => from && setFenster((f) => ({ ...f, from }))}
            w={150}
          />
          <DatePickerInput
            label={t("pages.settings.absences.windowTo")}
            valueFormat={GERMAN_DATE_FORMAT}
            value={fenster.to}
            onChange={(to) => to && setFenster((f) => ({ ...f, to }))}
            w={150}
          />
        </Group>
        <Button onClick={oeffneNeu}>{t("pages.settings.absences.add")}</Button>
      </Group>

      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          {t("pages.settings.absences.listTitle", {
            from: formatIsoDate(toLocalDateString(fenster.from)),
            to: formatIsoDate(toLocalDateString(fenster.to)),
          })}
        </Title>
        {eintraege.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t("pages.settings.absences.empty")}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("pages.settings.absences.colPerson")}</Table.Th>
                <Table.Th>{t("pages.settings.absences.colPeriod")}</Table.Th>
                <Table.Th>{t("pages.settings.absences.colDays")}</Table.Th>
                <Table.Th>{t("pages.settings.absences.colCreatedBy")}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {eintraege.map((eintrag) => (
                <Table.Tr key={eintrag.id}>
                  <Table.Td>{eintrag.employeeName}</Table.Td>
                  <Table.Td>
                    {formatIsoDate(eintrag.dateFrom)} –{" "}
                    {formatIsoDate(eintrag.dateTo)}
                  </Table.Td>
                  <Table.Td>
                    {t("pages.settings.absences.dayCount", {
                      count: eintrag.days,
                    })}
                    {eintrag.dayFraction === 0.5 ? (
                      <Badge ml="xs" variant="light" color="gray">
                        {t("pages.settings.absences.halfDay")}
                      </Badge>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {eintrag.createdByName ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      {/* Je Zeile ein Paar dieser Knoepfe. Der sichtbare
                          Text bleibt kurz, der Name nennt Person und
                          Zeitraum — sonst heissen in dieser Tabelle alle
                          Schaltflaechen gleich. */}
                      <Button
                        size="xs"
                        variant="light"
                        aria-label={t("pages.settings.absences.editAria", {
                          name: eintrag.employeeName,
                          from: formatIsoDate(eintrag.dateFrom),
                          to: formatIsoDate(eintrag.dateTo),
                        })}
                        onClick={() => oeffneBearbeiten(eintrag)}
                      >
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        loading={loeschen.isPending}
                        aria-label={t("pages.settings.absences.deleteAria", {
                          name: eintrag.employeeName,
                          from: formatIsoDate(eintrag.dateFrom),
                          to: formatIsoDate(eintrag.dateTo),
                        })}
                        onClick={() => void loeschen.mutateAsync(eintrag.id)}
                      >
                        {t("common.delete")}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {offen ? (
        <AbsenceModal
          key={bearbeitet?.id ?? "neu"}
          opened
          onClose={() => setOffen(false)}
          bearbeitet={bearbeitet}
          auswahl={employees.data ?? []}
        />
      ) : null}
    </Stack>
  );
}
